import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { PageHeader } from "../components/Layout";
import { toast } from "sonner";

export default function Organisation() {
  const [org, setOrg] = useState(null);
  const [priorities, setPriorities] = useState("");
  const [editing, setEditing] = useState(false);

  useEffect(() => {
    api.get("/organization").then((r) => { setOrg(r.data); setPriorities(r.data.strategic_priorities.join("\n")); });
  }, []);

  const save = async () => {
    const { data } = await api.put("/organization", { strategic_priorities: priorities.split("\n").filter(Boolean) });
    setOrg(data); setEditing(false);
    toast.success("Strategic priorities updated");
  };

  if (!org) return <div className="p-8">Loading…</div>;
  const facts = [
    ["Industry", `${org.industry} — ${org.sub_industry}`], ["Type", org.org_type],
    ["Regions", org.regions.join(", ")], ["Revenue", org.revenue],
    ["Headcount", org.headcount.toLocaleString()], ["Customers", org.customers],
    ["Digital maturity", `${org.maturity_digital} / 5`], ["AI maturity", `${org.maturity_ai} / 5`],
  ];

  return (
    <div className="canvas-grain min-h-screen pb-16" data-testid="organisation-page">
      <PageHeader overline="Organisation" title={org.name} subtitle="Organisation profile, strategic priorities, regulatory context and known constraints."
        actions={<Link to="/onboarding" data-testid="rerun-onboarding-btn" className="text-xs border border-black/20 rounded-sm px-3 py-2 hover:border-[#D4AF37] transition-colors">Re-run onboarding wizard</Link>} />
      <div className="px-8 grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="bg-white border border-black/10 rounded-sm p-6">
          <p className="text-[11px] uppercase tracking-[0.15em] text-black/50 mb-4">Profile</p>
          {facts.map(([k, v]) => (
            <div key={k} className="flex justify-between py-1.5 border-b border-black/5 last:border-0 text-[13px]">
              <span className="text-black/50">{k}</span><span className="font-medium text-right max-w-[60%]">{v}</span>
            </div>
          ))}
        </div>
        <div className="bg-white border border-black/10 rounded-sm p-6">
          <div className="flex items-center justify-between mb-4">
            <p className="text-[11px] uppercase tracking-[0.15em] text-black/50">Strategic priorities</p>
            {editing ? (
              <button onClick={save} data-testid="save-priorities-btn" className="text-xs bg-[#0A0A0A] text-[#D4AF37] px-3 py-1 rounded-sm">Save</button>
            ) : (
              <button onClick={() => setEditing(true)} data-testid="edit-priorities-btn" className="text-xs border border-black/20 px-3 py-1 rounded-sm hover:border-[#D4AF37] transition-colors">Edit</button>
            )}
          </div>
          {editing ? (
            <textarea value={priorities} onChange={(e) => setPriorities(e.target.value)} rows={8} data-testid="priorities-textarea"
              className="w-full border border-black/15 rounded-sm p-2 text-[13px] outline-none focus:ring-1 focus:ring-[#D4AF37]" />
          ) : (
            <ol className="space-y-2">
              {org.strategic_priorities.map((p, i) => (
                <li key={p} className="flex gap-2 text-[13px]"><span className="font-display font-bold text-[#D4AF37]">{i + 1}.</span>{p}</li>
              ))}
            </ol>
          )}
        </div>
        <div className="space-y-6">
          <div className="bg-white border border-black/10 rounded-sm p-6">
            <p className="text-[11px] uppercase tracking-[0.15em] text-black/50 mb-3">Regulatory environment</p>
            <div className="flex flex-wrap gap-2">
              {org.regulatory.map((r) => <span key={r} className="text-[12px] bg-red-50 text-red-700 border border-red-200 rounded-full px-3 py-1">{r}</span>)}
            </div>
          </div>
          <div className="bg-[#0A0A0A] text-white rounded-sm p-6">
            <p className="text-[11px] uppercase tracking-[0.15em] text-[#D4AF37] mb-3">Known pain points</p>
            <ul className="space-y-2">
              {org.pain_points.map((p) => <li key={p} className="text-[13px] text-white/80 flex gap-2"><span className="text-[#D4AF37]">—</span>{p}</li>)}
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}
